import Checkboxes from 'components/Form/Checkboxes';
import Modal from 'components/Modal';
import Truncator from 'components/Truncator';
import {FC, useCallback, useEffect} from 'react';
import {FormProvider, useForm, useFormContext} from 'react-hook-form';
import {useBackend} from 'state/backend';
import {c} from 'ttag';

import DocumentTypePills from '../../DocumentTypePills';
import {FavouriteTemplateFormFields} from '../../services';
import {
    FavouriteTemplateOption,
    FavouriteTemplatesEditBackendData,
} from '../../types';

type AddItemModalForm = {
    modal: Record<string, boolean>;
};

type AddItemModalProps = {
    isOpen: boolean;
    onClose: () => void;
};

const DEFAULT_VALUES = (elements: FavouriteTemplateOption[]) => ({
    modal: Object.fromEntries(elements.map((element) => [element, true])),
});

const AddItemModal: FC<AddItemModalProps> = ({isOpen, onClose}) => {
    const {
        data: {allTemplates, availableDocumentTypes, favouriteTemplates},
    } = useBackend<FavouriteTemplatesEditBackendData>();

    const {getValues, setValue} = useFormContext<FavouriteTemplateFormFields>();
    const methods = useForm<AddItemModalForm>({
        defaultValues: DEFAULT_VALUES(allTemplates),
    });

    const selection = methods.watch('modal');

    // Set Initial Modal Value
    useEffect(() => {
        if (isOpen) {
            const initialSelection = [...getValues().favouriteTemplates].map(
                (element) => element.id,
            );
            const newValue = Object.fromEntries(
                allTemplates.map((element) => [
                    element.id,
                    initialSelection.includes(element.id),
                ]),
            ) as AddItemModalForm['modal'];
            methods.setValue('modal', newValue);
        }
    }, [
        allTemplates,
        favouriteTemplates,
        getValues,
        isOpen,
        methods,
        setValue,
    ]);
    const onCancelModal = onClose;

    const onCloseModal = useCallback(() => {
        const initialSelection = [...getValues().favouriteTemplates];

        const existingElements = initialSelection.filter(
            (element) => selection[element.id],
        );

        const addedElements = allTemplates.filter(
            (element) =>
                selection[element.id] &&
                !existingElements
                    .map((existingElement) => existingElement.id)
                    .includes(element.id),
        );

        setValue('favouriteTemplates', [...existingElements, ...addedElements]);
        onClose();
    }, [allTemplates, getValues, onClose, selection, setValue]);

    const options = allTemplates.map((element) => ({
        label: (
            <div className="flex">
                <DocumentTypePills
                    availableDocumentTypes={availableDocumentTypes}
                    documentTypeDisplayNames={element.documentTypeDisplayNames}
                />
                <Truncator
                    className="ml-4 max-w-md"
                    text={element.templateDisplayName}
                />
            </div>
        ),
        name: `modal.${element.id}`,
    }));

    return (
        <FormProvider {...methods}>
            <Modal isOpen={isOpen} onClose={onCancelModal}>
                <Modal.Header>{c('component')
                    .t`Select Template(s)`}</Modal.Header>
                <Modal.Body>
                    <Checkboxes options={options} />
                </Modal.Body>
                <Modal.Footer>
                    <Modal.Button
                        kind="tertiary"
                        onClick={onCancelModal}
                        size="sm"
                        type="cancel"
                    >
                        {c('component').t`Cancel`}
                    </Modal.Button>
                    <Modal.Button
                        onClick={onCloseModal}
                        size="sm"
                        type="submit"
                    >
                        {c('component').t`OK`}
                    </Modal.Button>
                </Modal.Footer>
            </Modal>
        </FormProvider>
    );
};

export default AddItemModal;
